import {
  CHAIN_HASH_HEADER,
  CHUNK_SEQ_HEADER,
  PKIL_CONTENT_TYPE,
  PKIL_ENCODING_HEADER,
  PKIL_ENCODING_ZSTD,
  PlayerMe,
  SessionAdoptResponse,
  SessionStartResponse,
  type ApiError as ApiErrorBody,
  type AuthResponse,
  type CheckpointMeta,
  type GameVersion,
  type SessionAdoptRequest,
  type SessionStartRequest,
} from "@redchain/contracts";
import { apiBase } from "../env";
import { sseBus } from "./events";
import type {
  AuthNonce,
  AuthVerifyRequest,
  BindLinkCheckpointResponse,
  BuiltTx,
  BuyLevelsResponse,
  CloseResult,
  ItemsResult,
  JoinLobbyResponse,
  LobbyResult,
  LobbyStatusResponse,
  MonMetadata,
  MoneyMe,
  MonsResponse,
  MonSummary,
  RcdTransfersResponse,
  SignatureResult,
  TokenStats,
  VaultLedgerResponse,
} from "./types";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message ?? `${status} ${body.reason}`);
    this.name = "ApiRequestError";
  }
  get reason(): string {
    return this.body.reason;
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : "network error");
    this.name = "NetworkError";
  }
}

export function isApiError(e: unknown, reason?: string): e is ApiRequestError {
  return e instanceof ApiRequestError && (reason === undefined || e.body.reason === reason);
}

interface RequestOpts {
  json?: unknown;
  body?: BodyInit;
  headers?: Record<string, string>;
  auth?: boolean;
  keepalive?: boolean;
  signal?: AbortSignal;
  expect?: "json" | "none" | "blob" | "text";
}

const REFRESH_EVERY_MS = 12 * 60 * 1000;

/** Typed fetch wrapper for every §20 endpoint the web client uses. */
export class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<string | null> | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  onSignedOut: () => void = () => {};

  get token(): string | null {
    return this.accessToken;
  }

  setToken(token: string | null): void {
    this.accessToken = token;
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = token ? setInterval(() => void this.refresh(), REFRESH_EVERY_MS) : null;
  }

  /** Rotates the access token using the httpOnly refresh cookie. */
  refresh(): Promise<string | null> {
    this.refreshing ??= (async () => {
      try {
        const res = await fetch(`${apiBase}/auth/refresh`, { method: "POST", credentials: "include" });
        if (!res.ok) {
          this.setToken(null);
          this.onSignedOut();
          return null;
        }
        const { accessToken } = (await res.json()) as { accessToken: string };
        this.setToken(accessToken);
        return accessToken;
      } catch {
        return this.accessToken;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  async request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
    const doFetch = async (): Promise<Response> => {
      const headers: Record<string, string> = { ...(opts.headers ?? {}) };
      if (opts.auth !== false && this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;
      let body = opts.body;
      if (opts.json !== undefined) {
        headers["content-type"] = "application/json";
        body = JSON.stringify(opts.json);
      }
      try {
        return await fetch(`${apiBase}${path}`, {
          method,
          headers,
          body,
          credentials: "include",
          keepalive: opts.keepalive,
          signal: opts.signal,
        });
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") throw e;
        throw new NetworkError(e);
      }
    };

    let res = await doFetch();
    if (res.status === 401 && opts.auth !== false && !opts.keepalive) {
      const t = await this.refresh();
      if (t) res = await doFetch();
    }
    if (!res.ok) {
      let body: ApiErrorBody = { code: res.status, reason: `http_${res.status}` };
      try {
        const parsed = (await res.json()) as Partial<ApiErrorBody>;
        body = { ...body, ...parsed, code: parsed.code ?? res.status, reason: parsed.reason ?? body.reason };
      } catch {
        /* non-JSON error body */
      }
      throw new ApiRequestError(res.status, body);
    }
    switch (opts.expect ?? "json") {
      case "none":
        return undefined as T;
      case "blob":
        return (await res.blob()) as T;
      case "text":
        return (await res.text()) as T;
      default:
        if (res.status === 204) return undefined as T;
        return (await res.json()) as T;
    }
  }

  // ---- auth (§20.1)
  authNonce(wallet: string) {
    return this.request<AuthNonce>("POST", "/auth/nonce", { json: { wallet }, auth: false });
  }
  /** `publicKey`, `signedMessage` and `signature` must be base64 (see AuthVerifyRequest). */
  authVerify(body: AuthVerifyRequest) {
    return this.request<AuthResponse>("POST", "/auth/verify", { json: body, auth: false });
  }
  authPrivy(privyAccessToken: string) {
    return this.request<AuthResponse>("POST", "/auth/privy", { json: { privyAccessToken }, auth: false });
  }
  async logout() {
    await this.request<void>("POST", "/auth/logout", { expect: "none" }).catch(() => {});
    this.setToken(null);
  }

  // ---- players (§20.2)
  register(initialVaultLamports: string) {
    return this.request<BuiltTx>("POST", "/players/register", { json: { initialVaultLamports } });
  }
  registerSubmit(signedTransaction: string) {
    return this.request<SignatureResult>("POST", "/players/register/submit", { json: { signedTransaction } });
  }
  async me(): Promise<PlayerMe> {
    return PlayerMe.parse(await this.request<unknown>("GET", "/players/me"));
  }
  vaultDeposit(lamports: string) {
    return this.request<BuiltTx>("POST", "/players/me/vault/deposit", { json: { lamports } });
  }
  vaultWithdraw(lamports: string, payFeeIn: "RCD" | "SOL") {
    return this.request<BuiltTx>("POST", "/players/me/vault/withdraw", { json: { lamports, payFeeIn } });
  }
  vaultLedger() {
    return this.request<VaultLedgerResponse>("GET", "/players/me/vault/ledger");
  }
  rcdClaim(amount: number, confirmMidSession?: boolean) {
    return this.request<BuiltTx>("POST", "/players/me/rcd/claim", { json: { amount, ...(confirmMidSession ? { confirmMidSession } : {}) } });
  }
  rcdDeposit(amount: number, payFeeIn: "RCD" | "SOL") {
    return this.request<BuiltTx>("POST", "/players/me/rcd/deposit", { json: { amount, payFeeIn } });
  }
  /** Spending limit for the game on the wallet's ₽ (0 revokes). */
  rcdAllowance(amount: number, payFeeIn: "RCD" | "SOL") {
    return this.request<BuiltTx>("POST", "/players/me/rcd/allowance", { json: { amount, payFeeIn } });
  }
  rcdTransfers() {
    return this.request<RcdTransfersResponse>("GET", "/players/me/rcd/transfers");
  }
  items() {
    return this.request<ItemsResult>("GET", "/players/me/items");
  }
  itemsClaim(index: number, amount: number, payFeeIn: "RCD" | "SOL") {
    return this.request<BuiltTx>("POST", "/players/me/items/claim", { json: { index, amount, payFeeIn } });
  }
  itemsDeposit(index: number, amount: number, payFeeIn: "RCD" | "SOL", asset?: string) {
    return this.request<BuiltTx>("POST", "/players/me/items/deposit", { json: { index, amount, payFeeIn, ...(asset ? { asset } : {}) } });
  }
  relayBuild(kind: string, params: Record<string, unknown>) {
    return this.request<BuiltTx>("POST", "/relay/build", { json: { kind, params } });
  }
  relaySignAndSend(signedTransaction: string) {
    return this.request<SignatureResult>("POST", "/relay/sign-and-send", { json: { signedTransaction } });
  }
  close(confirmForfeit: boolean) {
    return this.request<CloseResult>("POST", "/players/me/close", { json: { confirmForfeit } });
  }
  burnMon(asset: string, refundAs: "rcd" | "sol") {
    return this.request<BuiltTx>("POST", `/mon/${encodeURIComponent(asset)}/burn`, { json: { refundAs } });
  }
  setRefundPref(releaseAs: "rcd" | "sol") {
    return this.request<BuiltTx>("POST", "/players/me/refund-pref", { json: { releaseAs } });
  }
  newGame(gameVersion: GameVersion) {
    return this.request<{ allowedAfter: string }>("POST", "/players/me/new-game", { json: { confirm: true, gameVersion } });
  }
  migrate(newWallet: string, newWalletProof: Parameters<ApiClient["authVerify"]>[0]) {
    return this.request<BuiltTx>("POST", "/players/me/migrate", { json: { newWallet, newWalletProof } });
  }
  migrateSubmit(signedTransaction: string) {
    return this.request<SignatureResult>("POST", "/players/me/migrate/submit", { json: { signedTransaction } });
  }
  ledgerCsv(from?: string, to?: string) {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString();
    return this.request<Blob>("GET", `/players/me/ledger.csv${qs ? `?${qs}` : ""}`, { expect: "blob" });
  }

  // ---- sessions (§20.3)
  async startSession(req: SessionStartRequest): Promise<SessionStartResponse> {
    return SessionStartResponse.parse(await this.request<unknown>("POST", "/sessions", { json: req }));
  }
  async adoptSession(req: SessionAdoptRequest): Promise<SessionAdoptResponse> {
    return SessionAdoptResponse.parse(await this.request<unknown>("POST", "/sessions/adopt", { json: req }));
  }
  uploadChunk(sessionId: string, c: { seq: number; bytes: Uint8Array; chainHash: string }, keepalive = false) {
    return this.request<void>("POST", `/sessions/${encodeURIComponent(sessionId)}/inputs`, {
      body: c.bytes as BodyInit,
      headers: {
        "content-type": PKIL_CONTENT_TYPE,
        [CHUNK_SEQ_HEADER]: String(c.seq),
        [CHAIN_HASH_HEADER]: c.chainHash,
        [PKIL_ENCODING_HEADER]: PKIL_ENCODING_ZSTD,
      },
      keepalive,
      expect: "none",
    });
  }
  uploadCheckpoint(sessionId: string, meta: CheckpointMeta, sramZst: Uint8Array, keepalive = false) {
    const form = new FormData();
    // A plain field: a Blob would be a second file part, and the API accepts one file (sram).
    form.append("meta", JSON.stringify(meta));
    form.append("sram", new Blob([sramZst as BlobPart], { type: "application/zstd" }), "sram.zst");
    return this.request<{ checkpointId: string }>("POST", `/sessions/${encodeURIComponent(sessionId)}/checkpoints`, { body: form, keepalive });
  }
  heartbeat(sessionId: string) {
    return this.request<void>("POST", `/sessions/${encodeURIComponent(sessionId)}/heartbeat`, { expect: "none" });
  }
  endSession(sessionId: string, keepalive = false) {
    return this.request<void>("POST", `/sessions/${encodeURIComponent(sessionId)}/end`, { expect: "none", keepalive });
  }
  eventsUrl(sessionId: string): string {
    return `${apiBase}/sessions/${encodeURIComponent(sessionId)}/events`;
  }

  // ---- public (§20.4)
  async mons(owner: string): Promise<MonSummary[]> {
    return (await this.monsWithRate(owner)).mons;
  }
  monsWithRate(owner: string) {
    return this.request<MonsResponse>("GET", `/mons?owner=${encodeURIComponent(owner)}`);
  }
  buyLevels(asset: string, targetLevel: number) {
    return this.request<BuiltTx & BuyLevelsResponse>("POST", `/mon/${encodeURIComponent(asset)}/buy-levels`, { json: { targetLevel } });
  }
  monMetadata(asset: string) {
    return this.request<MonMetadata>("GET", `/mon/${encodeURIComponent(asset)}.json`, { auth: false });
  }
  moneyMe() {
    return this.request<MoneyMe>("GET", "/money/me");
  }
  tokenStats() {
    return this.request<TokenStats>("GET", "/token/stats", { auth: false });
  }

  // ---- commerce
  rcdMarketQuote(body: { side: "buy" | "sell"; amountIn: string; slippageBps?: number }) {
    return this.request<{
      quoteId: string;
      side: "buy" | "sell";
      amountIn: string;
      expectedOut: string;
      minimumOutputAmount: string;
      poolFee: string;
      networkFeeEstimate: string;
      maxSolReserve?: string;
      slippageBps: number;
      expiresAt: string;
      contextSlot: number;
      rateLabel: string;
    }>("POST", "/rcd-market/quote", { json: body });
  }
  rcdMarketBuild(quoteId: string, idempotencyKey: string) {
    return this.request<{ swapId: string; transaction: string; minimumOutputAmount: string; expiresAt: string }>(
      "POST",
      "/rcd-market/build",
      { json: { quoteId, idempotencyKey } },
    );
  }
  rcdMarketSubmit(swapId: string, signedTransaction: string) {
    return this.request<SignatureResult>("POST", "/rcd-market/submit", { json: { swapId, signedTransaction } });
  }
  rcdMarketStatus(swapId: string) {
    return this.request<{
      swapId: string;
      status: string;
      signature: string | null;
      amountIn: string;
      amountOut: string | null;
      reason: string | null;
    }>("GET", `/rcd-market/swaps/${encodeURIComponent(swapId)}`);
  }
  marketListings(cursor?: string) {
    const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return this.request<{
      listings: Array<{
        listingId: string;
        asset: string;
        seller: string;
        priceRcd: string;
        status: string;
        species?: number | null;
        nickname?: string | null;
        level?: number | null;
        imageUrl?: string | null;
        details?: string[];
        createdAt: string;
      }>;
      cursor: string | null;
    }>("GET", `/market/listings${q}`, { auth: false });
  }
  marketEligibleAssets() {
    return this.request<{
      assets: Array<{
        asset: string;
        species: number | null;
        nickname: string | null;
        level: number | null;
        imageUrl: string | null;
        details: string[];
        eligible: boolean;
        reason: string | null;
      }>;
    }>("GET", "/market/eligible-assets");
  }
  marketList(asset: string, priceRcd: string, idempotencyKey: string) {
    return this.request<{ intentId: string; transaction: string; listingId?: string; priceRcd?: string }>("POST", "/market/list", {
      json: { asset, priceRcd, idempotencyKey, payFeeIn: "SOL" },
    });
  }
  marketBuy(listingId: string, idempotencyKey: string) {
    return this.request<{ intentId: string; transaction: string; listingId?: string; priceRcd?: string }>("POST", "/market/buy", {
      json: { listingId, idempotencyKey, payFeeIn: "SOL" },
    });
  }
  marketCancel(listingId: string, idempotencyKey: string) {
    return this.request<{ intentId: string; transaction: string }>("POST", "/market/cancel", {
      json: { listingId, idempotencyKey, payFeeIn: "SOL" },
    });
  }
  marketSubmit(intentId: string, signedTransaction: string) {
    return this.request<SignatureResult>("POST", "/market/submit", { json: { intentId, signedTransaction } });
  }
  shopCatalog() {
    return this.request<{
      products: Array<{
        productId: string;
        sku: string;
        name: string;
        kind: "item" | "mon";
        priceRcd: string;
        stock: number;
        version: number;
        imageUrl?: string | null;
        details?: string[];
        itemIndex?: number | null;
      }>;
    }>("GET", "/shop/catalog", { auth: false });
  }
  shopPurchase(productId: string, productVersion: number, idempotencyKey: string, quantity = 1) {
    return this.request<{ intentId: string; transaction: string; totalPriceRcd: string }>("POST", "/shop/purchase", {
      json: { productId, productVersion, quantity, idempotencyKey, payFeeIn: "SOL" },
    });
  }
  shopSubmit(intentId: string, signedTransaction: string) {
    return this.request<SignatureResult>("POST", "/shop/submit", { json: { intentId, signedTransaction } });
  }

  // ---- link (§20.5, Phase 4)
  createLobby(mode: "trade" | "battle" | "wager_battle", serverRun = false) {
    return this.request<LobbyResult>("POST", "/link/lobbies", { json: { mode, ...(serverRun ? { serverRun } : {}) } });
  }
  joinLobby(code: string) {
    return this.request<JoinLobbyResponse>("POST", `/link/lobbies/${encodeURIComponent(code)}/join`, { json: {} });
  }
  lobby(lobbyId: string) {
    return this.request<LobbyStatusResponse>("GET", `/link/lobbies/${encodeURIComponent(lobbyId)}`);
  }
  /** Binds this player's Cable Club checkpoint (the forced save) to its side of the lobby. */
  bindLinkCheckpoint(lobbyId: string, checkpointId: string) {
    return this.request<BindLinkCheckpointResponse>("POST", `/link/lobbies/${encodeURIComponent(lobbyId)}/checkpoint`, { json: { checkpointId } });
  }
  /** WebSocket URL for `/v1/link/ws` on the API origin; the token travels as the `bearer.<jwt>` subprotocol. */
  linkWsUrl(lobbyId: string): string {
    const base = new URL(apiBase, window.location.href);
    base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
    return `${base.toString().replace(/\/$/, "")}/link/ws?lobby=${encodeURIComponent(lobbyId)}`;
  }
}

export const api = new ApiClient();

export const SYNC_PENDING_TEXT = "Waiting for your last save to finish…";

/**
 * Runs a request that may hit `409 sync_pending` (§15.5.4). While waiting it reports
 * `true` through `onWaiting` (the overlay shows SYNC_PENDING_TEXT), then retries on
 * `sync.pending_cleared` or after `retryAfterMs`, whichever comes first.
 */
export async function withSyncPendingRetry<T>(fn: () => Promise<T>, onWaiting: (waiting: boolean) => void, maxAttempts = 10): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      const out = await fn();
      onWaiting(false);
      return out;
    } catch (e) {
      if (!isApiError(e, "sync_pending") || attempt >= maxAttempts) {
        onWaiting(false);
        throw e;
      }
      onWaiting(true);
      await sseBus.next("sync.pending_cleared", e.body.retryAfterMs ?? 5_000);
    }
  }
}
